import { useState, useEffect, useRef } from "react";
import { useDisclosure } from "@chakra-ui/react";
import {
  MotionBox,
  MotionFlex,
  MotionContainer,
  MotionStack,
  navbarAnimation,
} from "utils/motion";
import LanguageSelector from "components/blocks/LanguageSelector";
import Link from "components/base/Link";
import Button from "components/base/Button";
import Menu from "components/icons/Menu";
import Close from "components/icons/Close";
import Trans from "components/base/Trans";
import Image from "components/base/Image";
import NavbarMenu from "components/blocks/NavbarMenu";
import { useCookie } from "utils/cookieHelper";
import { useRouter } from "next/router";

import navbarContent from "utils/navbar.json";

const onAccountClick = (router, authToken) => {
  if (authToken) {
    router.push("/account");
  } else {
    router.push("/auth");
  }
};

export default function Navbar({
  initialized,
  hasGradient,
  isInverted,
  isFixed,
  noMenu,
  forceHide,
  ...props
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [contentSlide, setContentSlide] = useState(0);
  const [goingUp, setGoingUp] = useState(false);
  const cookie = useCookie(props.cookie);
  const authToken = cookie.get("authToken");
  const router = useRouter();
  const prevScrollY = useRef(0);

  let navbarBg = null;
  let bgFilter = null;

  // Scroll-reveal for the fixed navbar. Hooks must be unconditional, so the
  // isFixed check lives inside the effect.
  useEffect(() => {
    if (!isFixed) return undefined;

    const handleScroll = () => {
      const curScrollY = window.scrollY;
      if ((prevScrollY.current < curScrollY && goingUp) || curScrollY < 200) {
        setGoingUp(false);
      }
      if (prevScrollY.current > curScrollY && !goingUp && curScrollY > 200) {
        setGoingUp(true);
      }
      prevScrollY.current = curScrollY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, [goingUp, isFixed]);

  if (isFixed) {
    navbarBg = "black.700";
    bgFilter = "blur(5px)";
  } else {
    navbarBg = hasGradient
      ? "linear-gradient(to top, rgba(0,0,0,0), rgba(0,0,0,1))"
      : "transparent";
    bgFilter = hasGradient
      ? "none"
      : navbarBg !== "transparent"
        ? "blur(5px)"
        : "none";
  }

  const onAction = (slide) => {
    setContentSlide(slide);
    onOpen();
  };

  return (
    <MotionBox
      width="100%"
      position="absolute"
      top="0"
      display="flex"
      justifyContent="center"
    >
      <MotionBox
        variants={navbarAnimation}
        // The gradient navbar is part of the first paint: starting it hidden
        // (y: -192) made it drop in only after hydration. Only the fixed
        // scroll-reveal navbar starts hidden.
        initial={isFixed || forceHide ? "hidden" : "visible"}
        position={isFixed ? "fixed" : "absolute"}
        left={0}
        zIndex={1000}
        width="100%"
        bg={navbarBg}
        // A backdrop-filter over the hero video forces the compositor to
        // re-read the video frame every paint. It is only needed once the
        // fixed bar is actually revealed while scrolling up.
        backdropFilter={isFixed && !goingUp ? "none" : bgFilter}
        animate={
          forceHide
            ? "hidden"
            : isFixed
              ? goingUp
                ? "visible"
                : "hidden"
              : initialized
                ? "visible"
                : "hidden"
        }
      >
        <MotionContainer
          px={[6, 6, 4, 10]}
          py={[4, null, 4]}
          maxWidth="100%"
          margin="auto"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          direction="row"
        >
          <MotionStack isInline align="center" spacing={[4, 4, 4, 8]}>
            <Link
              href="/"
              pr={[0, 0, 0, 0, "24px", "32px"]}
              _focus={{ boxShadow: "0 0 0" }}
            >
              <Image
                priority
                _hover={{ cursor: "pointer" }}
                zIndex={2}
                src={
                  isInverted && !isOpen
                    ? "/images/logo-hill-navbar-black.png"
                    : "/images/logo-hill-navbar.png"
                }
                alt="logo"
                width={["132px", "132px", null, "203px"]}
                height={["39px", "39px", null, "60px"]}
                sizes="(max-width: 768px) 132px, 203px"
              />
            </Link>
            {noMenu === true
              ? null
              : navbarContent.map((e, idx) => (
                  <Button
                    id={e.id}
                    key={e.id}
                    display={["none", null, "flex"]}
                    onClick={e.items.length > 0 ? () => onAction(idx) : null}
                    onHoverStart={
                      e.items.length > 0 ? () => onAction(idx) : null
                    }
                    colorScheme={isInverted && !isOpen ? "black" : null}
                    href={e.url}
                    variant="link"
                    size="small"
                  >
                    <Trans id={e.label} />
                  </Button>
                ))}
          </MotionStack>
          <MotionFlex
            display={["none", null, noMenu ? null : "flex"]}
            align="center"
          >
            <Button
              colorScheme={isInverted && !isOpen ? "black" : null}
              variant="link"
              size="small"
              mr={["sm", "sm", "sm", "lg"]}
              onClick={() => onAccountClick(router, authToken)}
            >
              <Trans id="LBL_ACCOUNT" />
            </Button>
            <MotionBox mr={["10px", "10px", "10px", "20px"]}>
              <LanguageSelector
                small
                mb="3px"
                color={isInverted && !isOpen ? "black.900" : "white"}
              />
            </MotionBox>
            <Button
              id="configurator"
              href="/configurator/0"
              size="small"
              variant="outline"
              colorScheme={isInverted && !isOpen ? "black" : null}
              maxWidth={[140, 140, 140, 160]}
              height="42px"
            >
              <Trans id="LBL_CONFIGURATOR" />
            </Button>
          </MotionFlex>
          <MotionFlex display={[noMenu ? "none" : "flex", null, "none"]}>
            <MotionBox
              mr="6px"
              style={{
                opacity: isOpen ? 0 : 1,
                transition: "0.2 ease",
                pointerEvents: isOpen ? "none" : "all",
              }}
            >
              <LanguageSelector
                small
                mb="3px"
                color={isInverted ? "black.900" : "white"}
              />
            </MotionBox>
            <MotionBox
              _hover={{ cursor: "pointer" }}
              onClick={isOpen ? onClose : onOpen}
              id="navbar-menu"
            >
              {isOpen ? (
                <Close />
              ) : (
                <Menu color={isInverted ? "black.900" : "white"} />
              )}
            </MotionBox>
          </MotionFlex>
        </MotionContainer>
        <NavbarMenu
          onClose={onClose}
          isOpen={isOpen}
          contentSlide={contentSlide}
          onAction={(slide) => setContentSlide(slide)}
          onAccountClick={() => onAccountClick(router, authToken)}
        />
      </MotionBox>
    </MotionBox>
  );
}
